import { useState, useMemo, useCallback, useEffect, useRef, memo } from "react";
import { useNavigate } from "react-router-dom";
import { apiFetch } from "../../context/AppContext";
import { HelpTooltip } from "../../components/common/UI";
import { DEFAULT_COMPLIANCE } from "../../constants/complianceRegions";
import {
  ComposableMap,
  Geographies,
  Geography,
  ZoomableGroup,
  Marker,
  Annotation,
} from "react-simple-maps";
import countries110m from "world-atlas/countries-110m.json";
import {
  AlertTriangle,
  CheckCircle2,
  ChevronRight,
  FileText,
  Globe,
  LocateFixed,
  MapPin,
  Minus,
  PanelRight,
  Plus,
  Shield,
  TrendingUp,
  X,
} from "lucide-react";

/* Light, low-contrast map palette: land reads as a soft blue, everything else recedes. */
/* Countries are painted by compliance status. Deeper shade = a detailed score is available. */
const STATUS_FILL = {
  g: { base: "#5CC496", strong: "#2FA070", hover: "#22855B" },
  a: { base: "#F7C165", strong: "#EBA033", hover: "#CF8620" },
  r: { base: "#EE8A8E", strong: "#DE5158", hover: "#C23C44" },
  b: { base: "#8DBBEF", strong: "#5A93DA", hover: "#3F78C0" },
};
const MAP_LAND = "#A9C6E8"; /* in scope, no status yet */
const MAP_LAND_HOVER = "#8DB2DD";
const MAP_LAND_DIM = "#CFDBE9"; /* not in scope */
const MAP_LAND_DIM_HOVER = "#BFCFE2";
const MAP_STROKE = "#F5F9FE";
const MAP_LABEL = "#0B2545";
const MAP_OCEAN = "#E3EDF8";

const EU_MEMBERS = [
  "Austria", "Belgium", "Bulgaria", "Croatia", "Cyprus", "Czechia", "Denmark", "Estonia", "Finland",
  "France", "Germany", "Greece", "Hungary", "Ireland", "Italy", "Latvia", "Lithuania", "Luxembourg",
  "Netherlands", "Poland", "Portugal", "Romania", "Slovakia", "Slovenia", "Spain", "Sweden", "Malta",
];
const LABEL_ALIAS = {
  "United States of America": "United States",
  "United Arab Emirates": "UAE",
  "United Kingdom": "UK",
  "Dem. Rep. Congo": "DR Congo",
  "Central African Rep.": "C. African Rep.",
  "Bosnia and Herz.": "Bosnia",
  "Dominican Rep.": "Dominican Rep.",
};
const TINY_MARKERS = new Set(["Singapore", "Malta", "Luxembourg", "Cyprus"]); /* too small to see as a filled country */

const INDIA_OFFICIAL_GEOJSON_URL =
  "https://raw.githubusercontent.com/AbhinavSwami28/india-official-geojson/main/india-states-simplified.geojson";

const REGION_FOCUS = {
  "Global View": { coordinates: [70, 15], zoom: 1.4 },
  "Middle East": { coordinates: [50, 26], zoom: 3.4 },
  Africa: { coordinates: [18, 2], zoom: 2.2 },
  "Asia Pacific": { coordinates: [90, 15], zoom: 2.4 },
  Europe: { coordinates: [10, 50], zoom: 2.6 },
  "North America": { coordinates: [-98, 39], zoom: 2.2 },
};

const GEO_TO_UAE = { Dubai: "United Arab Emirates" };

const GEO_TO_MARKER = {
  "United States of America": "United States",
  "United Arab Emirates": "Dubai",
};

const MARKERS = [
  {"name":"India","s":"b","score":null,"regs":["DPDP Act"],"region":"Asia Pacific","featured":true,"dx":36,"dy":-16,"coordinates":[78.9,22.0]},
  {"name":"Saudi Arabia","s":"g","score":null,"regs":["PDPL"],"region":"Middle East","featured":true,"dx":-20,"dy":-14,"coordinates":[45.0,24.0]},
  {"name":"Kuwait","s":"g","score":null,"regs":["CITRA DPPR"],"region":"Middle East","featured":false,"dx":0,"dy":0,"coordinates":[47.5,29.3]},
  {"name":"Qatar","s":"g","score":null,"regs":["PDPPL"],"region":"Middle East","featured":false,"dx":0,"dy":0,"coordinates":[51.2,25.3]},
  {"name":"Bahrain","s":"g","score":null,"regs":["PDPL"],"region":"Middle East","featured":false,"dx":0,"dy":0,"coordinates":[50.6,26.1]},
  {"name":"Dubai","s":"g","score":null,"regs":["UAE PDPL"],"region":"Middle East","featured":true,"dx":24,"dy":8,"coordinates":[55.3,25.2]},
  {"name":"Nigeria","s":"a","score":null,"regs":["NDPA"],"region":"Africa","featured":true,"dx":-18,"dy":-8,"coordinates":[8.7,9.1]},
  {"name":"South Africa","s":"a","score":null,"regs":["POPIA"],"region":"Africa","featured":true,"dx":20,"dy":12,"coordinates":[25.0,-29.0]},
  {"name":"Malaysia","s":"g","score":null,"regs":["PDPA"],"region":"Asia Pacific","featured":true,"dx":-16,"dy":10,"coordinates":[101.9,4.2]},
  {"name":"Indonesia","s":"b","score":null,"regs":["PDP Law"],"region":"Asia Pacific","featured":false,"dx":0,"dy":0,"coordinates":[118.0,-2.5]},
  {"name":"Singapore","s":"g","score":null,"regs":["PDPA"],"region":"Asia Pacific","featured":true,"dx":28,"dy":10,"coordinates":[103.8,1.35]},
  {"name":"Philippines","s":"b","score":null,"regs":["DPA"],"region":"Asia Pacific","featured":true,"dx":22,"dy":-8,"coordinates":[122.0,12.0]},
  {"name":"Hong Kong","s":"g","score":null,"regs":["PDPO"],"region":"Asia Pacific","featured":true,"dx":18,"dy":-12,"coordinates":[114.2,22.3]},
  {"name":"European Union","s":"g","score":null,"regs":["GDPR"],"region":"Europe","featured":true,"dx":-24,"dy":-16,"coordinates":[10.5,50.5]},
  {"name":"Austria","s":"g","score":null,"regs":["GDPR"],"region":"Europe","featured":false,"dx":0,"dy":0,"coordinates":[16.4,47.5]},
  {"name":"Belgium","s":"g","score":null,"regs":["GDPR"],"region":"Europe","featured":false,"dx":0,"dy":0,"coordinates":[4.5,50.5]},
  {"name":"Bulgaria","s":"g","score":null,"regs":["GDPR"],"region":"Europe","featured":false,"dx":0,"dy":0,"coordinates":[25.5,42.7]},
  {"name":"Croatia","s":"g","score":null,"regs":["GDPR"],"region":"Europe","featured":false,"dx":0,"dy":0,"coordinates":[15.2,45.1]},
  {"name":"Cyprus","s":"g","score":null,"regs":["GDPR"],"region":"Europe","featured":false,"dx":0,"dy":0,"coordinates":[33.4,35.1]},
  {"name":"Czechia","s":"g","score":null,"regs":["GDPR"],"region":"Europe","featured":false,"dx":0,"dy":0,"coordinates":[15.5,49.8]},
  {"name":"Denmark","s":"g","score":null,"regs":["GDPR"],"region":"Europe","featured":false,"dx":0,"dy":0,"coordinates":[10.0,56.0]},
  {"name":"Estonia","s":"g","score":null,"regs":["GDPR"],"region":"Europe","featured":false,"dx":0,"dy":0,"coordinates":[25.0,58.6]},
  {"name":"Finland","s":"g","score":null,"regs":["GDPR"],"region":"Europe","featured":true,"dx":18,"dy":-10,"coordinates":[26.0,64.0]},
  {"name":"France","s":"g","score":null,"regs":["GDPR"],"region":"Europe","featured":true,"dx":-18,"dy":8,"coordinates":[2.2,46.2]},
  {"name":"Germany","s":"g","score":null,"regs":["GDPR"],"region":"Europe","featured":true,"dx":16,"dy":-12,"coordinates":[10.5,51.2]},
  {"name":"Greece","s":"g","score":null,"regs":["GDPR"],"region":"Europe","featured":true,"dx":16,"dy":10,"coordinates":[21.8,39.1]},
  {"name":"Hungary","s":"g","score":null,"regs":["GDPR"],"region":"Europe","featured":false,"dx":0,"dy":0,"coordinates":[19.5,47.2]},
  {"name":"Ireland","s":"g","score":null,"regs":["GDPR"],"region":"Europe","featured":true,"dx":-18,"dy":-10,"coordinates":[-8.2,53.3]},
  {"name":"Italy","s":"g","score":null,"regs":["GDPR"],"region":"Europe","featured":true,"dx":18,"dy":10,"coordinates":[12.6,42.5]},
  {"name":"Latvia","s":"g","score":null,"regs":["GDPR"],"region":"Europe","featured":false,"dx":0,"dy":0,"coordinates":[24.6,56.9]},
  {"name":"Lithuania","s":"g","score":null,"regs":["GDPR"],"region":"Europe","featured":false,"dx":0,"dy":0,"coordinates":[23.9,55.2]},
  {"name":"Luxembourg","s":"g","score":null,"regs":["GDPR"],"region":"Europe","featured":false,"dx":0,"dy":0,"coordinates":[6.1,49.8]},
  {"name":"Malta","s":"g","score":null,"regs":["GDPR"],"region":"Europe","featured":false,"dx":0,"dy":0,"coordinates":[14.4,35.9]},
  {"name":"Netherlands","s":"g","score":null,"regs":["GDPR"],"region":"Europe","featured":false,"dx":0,"dy":0,"coordinates":[5.3,52.1]},
  {"name":"Poland","s":"g","score":null,"regs":["GDPR"],"region":"Europe","featured":true,"dx":20,"dy":-8,"coordinates":[19.1,51.9]},
  {"name":"Portugal","s":"g","score":null,"regs":["GDPR"],"region":"Europe","featured":true,"dx":-20,"dy":10,"coordinates":[-8.2,39.4]},
  {"name":"Romania","s":"g","score":null,"regs":["GDPR"],"region":"Europe","featured":false,"dx":0,"dy":0,"coordinates":[25.0,45.9]},
  {"name":"Slovakia","s":"g","score":null,"regs":["GDPR"],"region":"Europe","featured":false,"dx":0,"dy":0,"coordinates":[19.7,48.7]},
  {"name":"Slovenia","s":"g","score":null,"regs":["GDPR"],"region":"Europe","featured":false,"dx":0,"dy":0,"coordinates":[14.8,46.1]},
  {"name":"Spain","s":"g","score":null,"regs":["GDPR"],"region":"Europe","featured":true,"dx":-16,"dy":12,"coordinates":[-3.7,40.5]},
  {"name":"Sweden","s":"g","score":null,"regs":["GDPR"],"region":"Europe","featured":true,"dx":18,"dy":-12,"coordinates":[15.0,62.0]},
  {"name":"United States","s":"g","score":null,"regs":["CCPA"],"region":"North America","featured":true,"dx":-24,"dy":-12,"coordinates":[-98.5,39.8]},
  {"name":"Canada","s":"g","score":null,"regs":["PIPEDA"],"region":"North America","featured":true,"dx":-20,"dy":-14,"coordinates":[-96.0,56.0]},
  {"name":"Mexico","s":"g","score":null,"regs":["LFPDPPP"],"region":"North America","featured":true,"dx":16,"dy":10,"coordinates":[-102.0,23.0]}
];

const STATUS_COLOR = { g: "#16A34A", a: "#D97706", r: "#DC2626", b: "#2563EB" };
const STATUS_LABEL = { g: "Fully Compliant", a: "Needs Attention", r: "Non-Compliant", b: "Monitoring" };
const STATUS_SHORT = { g: "Compliant", a: "At risk", r: "Non-compliant", b: "Monitoring" };
const STATUS_TIPS = {
  g: "Fully compliant: the privacy requirements for this country are met.",
  a: "Needs attention: some requirements are incomplete and should be reviewed.",
  r: "Non-compliant: important requirements are not met.",
  b: "Monitoring: this country is being watched, but a full compliance result is not ready yet.",
};

const REGION_STATS = [
  { name: "Middle East", pct: null },
  { name: "Africa", pct: null },
  { name: "Asia Pacific", pct: null },
  { name: "Europe", pct: null },
  { name: "North America", pct: null },
];

const VIEW_OPTIONS = ["Global View", "Middle East", "Africa", "Asia Pacific", "Europe", "North America"];

const scoreTone = (pct) => (pct >= 90 ? "#059669" : pct >= 75 ? "#D97706" : "#DC2626");
const FOCUS = "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1";

/* Scoped styles: typeface, map dot-grid, marker pulse, small reveal. Reduced motion respected. */
const STYLES = `
@import url('https://fonts.googleapis.com/css2?family=Manrope:wght@400;500;600;700;800&display=swap');
.gc-root{font-family:'Manrope',ui-sans-serif,system-ui,-apple-system,'Segoe UI',sans-serif;font-variant-numeric:tabular-nums}
.gc-dots{background-color:${MAP_OCEAN};background-image:radial-gradient(rgba(30,95,168,.16) 1px,transparent 1px);background-size:18px 18px}
.gc-pulse{transform-box:fill-box;transform-origin:center;animation:gc-pulse 1.8s ease-out infinite}
@keyframes gc-pulse{0%{transform:scale(.7);opacity:.6}100%{transform:scale(2.6);opacity:0}}
.gc-pop{animation:gc-pop .22s ease-out}
@keyframes gc-pop{from{opacity:0;transform:translateY(-6px)}to{opacity:1;transform:none}}
.gc-scroll::-webkit-scrollbar{width:6px;height:6px}
.gc-scroll::-webkit-scrollbar-thumb{background:#CBD5E1;border-radius:9999px}
.gc-noscroll{scrollbar-width:none}
.gc-noscroll::-webkit-scrollbar{display:none}
.gc-pin:focus-visible .gc-focus{stroke:#2563EB}
@media (prefers-reduced-motion:reduce){.gc-pulse,.gc-pop{animation:none}}
`;

/* ------------------------------------------------------------------ */
/* Map                                                                 */
/* ------------------------------------------------------------------ */
const ComplianceMap = memo(function ComplianceMap({
  markers, allowedNames, regionByCountry, regionView, statusFilter, activeName, dimmed, onEnter, onLeave, onPick, position, onMoveEnd,
}) {
  const [india, setIndia] = useState(null);
  useEffect(() => {
    let cancelled = false;
    fetch(INDIA_OFFICIAL_GEOJSON_URL)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((geo) => { if (!cancelled) setIndia(geo); })
      .catch(() => { if (!cancelled) setIndia(null); });
    return () => { cancelled = true; };
  }, []);

  /* geography name -> marker. EU member states share the "European Union" marker; a country's own marker wins. */
  const markerByGeo = useMemo(() => {
    const map = new Map();
    const eu = markers.find((m) => m.name === "European Union");
    if (eu) EU_MEMBERS.forEach((n) => map.set(n, eu));
    markers.forEach((m) => map.set(m.name, m));
    return map;
  }, [markers]);

  const paintFor = (geoName) => {
    const key = GEO_TO_MARKER[geoName] || geoName;
    const m = markerByGeo.get(key);
    if (!m) {
      if (!allowedNames.has(key)) return { fill: MAP_LAND_DIM, hover: MAP_LAND_DIM_HOVER, opacity: 1, m: null, active: false };
      const region = regionByCountry.get(key);
      const faded = (regionView !== VIEW_OPTIONS[0] && region && region !== regionView) || statusFilter != null;
      return { fill: MAP_LAND, hover: MAP_LAND_HOVER, opacity: faded ? 0.35 : 1, m: null, active: false };
    }
    const set = STATUS_FILL[m.s];
    return {
      fill: m.score != null ? set.strong : set.base,
      hover: set.hover,
      opacity: dimmed(m) ? 0.3 : 1,
      m,
      active: activeName === m.name,
    };
  };

  const renderGeo = (geo, geoName, strokeWidth) => {
    const p = paintFor(geoName);
    const cursor = p.m ? "pointer" : undefined;
    return (
      <Geography
        key={geo.rsmKey}
        geography={geo}
        fill={p.fill}
        stroke={p.active ? MAP_LABEL : MAP_STROKE}
        strokeWidth={p.active ? 1 : strokeWidth}
        onMouseEnter={p.m ? () => onEnter(p.m) : undefined}
        onMouseLeave={p.m ? onLeave : undefined}
        onClick={p.m ? () => onPick(p.m) : undefined}
        style={{
          default: { outline: "none", opacity: p.opacity, transition: "fill .2s, opacity .2s", cursor },
          hover: { outline: "none", fill: p.hover, opacity: p.opacity, cursor },
          pressed: { outline: "none", fill: p.hover, opacity: p.opacity },
        }}
      />
    );
  };

  /* Cached per-country label geometry: centroid + width of the largest landmass (projected units). */
  const statsRef = useRef(new Map());
  const geoStats = (geo, path) => {
    const cache = statsRef.current;
    if (cache.has(geo.rsmKey)) return cache.get(geo.rsmKey);
    let part = geo.geometry;
    if (part.type === "MultiPolygon") {
      let best = -1;
      part.coordinates.forEach((coords) => {
        const poly = { type: "Polygon", coordinates: coords };
        const a = path.area(poly);
        if (a > best) { best = a; part = poly; }
      });
    }
    const [x, y] = path.centroid(part);
    const [[x0], [x1]] = path.bounds(part);
    const stats = Number.isFinite(x) && Number.isFinite(y) ? { x, y, w: x1 - x0, area: path.area(part) } : null;
    cache.set(geo.rsmKey, stats);
    return stats;
  };

  /* Labels for every in-scope country. Constant on-screen size, biggest countries win, overlaps are skipped
     (or nudged up/down); more labels appear as you zoom in. */
  const buildLabels = (geographies, path, projection) => {
    const z = position.zoom;
    const F = 7.5;
    const charW = F * 0.6;
    const placed = [];
    const hit = (a, b) => Math.abs(a.x - b.x) < (a.w + b.w) / 2 && Math.abs(a.y - b.y) < (a.h + b.h) / 2;

    markers.filter((m) => m.featured).forEach((m) => {
      const pt = projection(m.coordinates);
      if (!pt) return;
      const w = m.name.length * 9 * 0.6 + 8;
      placed.push({
        x: pt[0] * z + (m.dx > 0 ? 1 : -1) * (22 + w / 2),
        y: pt[1] * z + (m.dy > 0 ? 12 : -12),
        w,
        h: 12,
      });
    });

    const cands = [];
    geographies.forEach((geo) => {
      const name = geo.properties.name;
      const key = GEO_TO_MARKER[name] || name;
      const m = markerByGeo.get(key);
      if (m && m.featured) return; /* already has a leader-line label */
      if (!m && !allowedNames.has(key)) return;
      const st = geoStats(geo, path);
      if (st) cands.push({ name, key, st });
    });
    cands.sort((a, b) => b.st.area - a.st.area);

    const out = [];
    cands.forEach(({ name, key, st }) => {
      const text = LABEL_ALIAS[name] || name;
      const w = text.length * charW + 4;
      if (st.w * z < w * 0.25 && z < 2) return;
      for (const off of [0, 9, -9]) {
        const rect = { x: st.x * z, y: st.y * z + off, w, h: 10 };
        if (placed.some((r) => hit(r, rect))) continue;
        placed.push(rect);
        out.push(
          <text
            key={`lbl-${key}`}
            x={st.x}
            y={st.y + off / z}
            textAnchor="middle"
            dominantBaseline="central"
            fontSize={F / z}
            fontWeight={activeName === key ? 800 : 700}
            fill={MAP_LABEL}
            stroke="#F5F9FE"
            strokeWidth={2.6 / z}
            strokeLinejoin="round"
            paintOrder="stroke"
            opacity={Math.max(paintFor(name).opacity, 0.45)}
            pointerEvents="none"
            aria-hidden="true"
          >
            {text}
          </text>,
        );
        break;
      }
    });
    return out;
  };

  return (
    <ComposableMap
      projection="geoEqualEarth"
      projectionConfig={{ scale: 155 }}
      width={800}
      height={400}
      style={{ width: "100%", height: "100%", display: "block" }}
    >
      <ZoomableGroup
        zoom={position.zoom}
        center={position.coordinates}
        onMoveEnd={onMoveEnd}
        minZoom={1}
        maxZoom={6}
      >
        <Geographies geography={countries110m}>
          {({ geographies, path, projection }) => (
            <>
              {geographies.map((geo) => {
                if (geo.id === "356" && india) return null;
                return renderGeo(geo, geo.properties.name, 0.5);
              })}
              {buildLabels(geographies, path, projection)}
            </>
          )}
        </Geographies>
        {india && (
          <Geographies geography={india}>
            {({ geographies }) => geographies.map((geo) => renderGeo(geo, "India", 0.3))}
          </Geographies>
        )}
        {/* Pins are only a keyboard/touch target now; a visible dot shows for tiny countries and the selection. */}
        {markers.map((m) => {
          const active = activeName === m.name;
          const tiny = TINY_MARKERS.has(m.name);
          return (
            <Marker key={m.name} coordinates={m.coordinates}>
              <g
                className="gc-pin"
                role="button"
                tabIndex={0}
                aria-label={`${m.name}: ${STATUS_LABEL[m.s]}`}
                onMouseEnter={() => onEnter(m)}
                onMouseLeave={onLeave}
                onFocus={() => onEnter(m)}
                onBlur={onLeave}
                onClick={() => onPick(m)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onPick(m); }
                }}
                style={{ cursor: "pointer", opacity: dimmed(m) ? 0.35 : 1, transition: "opacity .2s", outline: "none" }}
              >
                <circle className="gc-focus" r={8} fill="transparent" stroke="transparent" strokeWidth={1.5} />
                {active && <circle r={5} fill="none" stroke={MAP_LABEL} strokeWidth={1.3} className="gc-pulse" />}
                {(tiny || active) && (
                  <circle
                    r={tiny ? 3.6 : 2.6}
                    fill={tiny ? STATUS_FILL[m.s].strong : "#fff"}
                    stroke={tiny ? "#fff" : MAP_LABEL}
                    strokeWidth={1.3}
                  />
                )}
              </g>
            </Marker>
          );
        })}
        {markers.filter((m) => m.featured).map((m) => (
          <Annotation
            key={`label-${m.name}`}
            subject={m.coordinates}
            dx={(m.dx > 0 ? 18 : -18) / position.zoom}
            dy={(m.dy > 0 ? 12 : -12) / position.zoom}
            connectorProps={{ stroke: MAP_LABEL, strokeWidth: 0.8 / position.zoom, strokeOpacity: 0.55 }}
          >
            <text
              x={(m.dx > 0 ? 4 : -4) / position.zoom}
              textAnchor={m.dx > 0 ? "start" : "end"}
              fontSize={9 / position.zoom}
              fontWeight={activeName === m.name ? 800 : 700}
              fill={MAP_LABEL}
              stroke="#F5F9FE"
              strokeWidth={3 / position.zoom}
              strokeLinejoin="round"
              paintOrder="stroke"
              opacity={dimmed(m) ? 0.4 : 1}
              onClick={() => onPick(m)}
              style={{ cursor: "pointer" }}
            >
              {m.name}
            </text>
          </Annotation>
        ))}
      </ZoomableGroup>
    </ComposableMap>
  );
});

/* ------------------------------------------------------------------ */
/* Small pieces                                                        */
/* ------------------------------------------------------------------ */
const StatCell = memo(function StatCell({ icon: Icon, value, label, tone, progress, tooltip }) {
  return (
    <div className="relative flex items-center gap-3 bg-white px-4 py-3 transition-colors hover:bg-slate-50">
      <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${tone}`}>
        <Icon className="h-[18px] w-[18px]" />
      </span>
      <div className="min-w-0">
        <div className="text-xl font-bold leading-none tracking-tight text-slate-900">{value}</div>
        <div className="mt-1.5 flex min-w-0 items-center gap-1">
          <span className="truncate text-xs font-medium text-slate-500">{label}</span>
          {tooltip ? <HelpTooltip content={tooltip} size={12} position="bottom" className="shrink-0" /> : null}
        </div>
      </div>
      {progress != null && (
        <span className="absolute inset-x-0 bottom-0 h-0.5 bg-slate-100">
          <span className="block h-full bg-emerald-500 transition-[width] duration-700" style={{ width: `${progress}%` }} />
        </span>
      )}
    </div>
  );
});

const SectionTitle = ({ icon: Icon, children, action, tooltip }) => (
  <div className="mb-2 flex items-center justify-between gap-2">
    <h3 className="flex min-w-0 items-center gap-2 text-sm font-bold text-slate-900">
      <Icon className="h-4 w-4 shrink-0 text-slate-400" />
      <span className="truncate">{children}</span>
      {tooltip ? <HelpTooltip content={tooltip} size={13} position="bottom" className="shrink-0" /> : null}
    </h3>
    {action}
  </div>
);

const RegionBar = memo(function RegionBar({ name, pct, animated, active, onSelect }) {
  const tone = pct == null ? "#64748B" : scoreTone(pct);
  return (
    <button
      type="button"
      onClick={() => onSelect(name)}
      aria-pressed={active}
      title={`Focus map on ${name}`}
      className={`grid w-full grid-cols-[6rem_1fr_2.25rem] items-center gap-3 rounded-lg px-2 py-1.5 text-left text-xs transition-colors ${FOCUS} ${active ? "bg-blue-50" : "hover:bg-slate-50"}`}
    >
      <span className={`truncate font-semibold ${active ? "text-blue-700" : "text-slate-700"}`}>{name}</span>
      <span className="h-1.5 overflow-hidden rounded-full bg-slate-100">
        <span
          className="block h-full rounded-full transition-[width] duration-1000 ease-out"
          style={{ width: animated && pct != null ? `${pct}%` : "0%", background: tone }}
        />
      </span>
      <span className="text-right font-bold text-slate-800">{pct == null ? "—" : `${pct}%`}</span>
    </button>
  );
});

const FrameworkRow = memo(function FrameworkRow({ fw, active, onSelect, onOpenChecklist }) {
  const tone = fw.pct == null ? "#64748B" : scoreTone(fw.pct);
  return (
    <div className={`flex items-center gap-0.5 rounded-lg transition-colors ${active ? "bg-blue-50" : "hover:bg-slate-50"}`}>
      <button
        type="button"
        onClick={() => onSelect(fw)}
        aria-pressed={active}
        title={`Show ${fw.region} on the map`}
        className={`flex min-w-0 flex-1 items-center gap-3 rounded-lg px-2 py-2 text-left ${FOCUS}`}
      >
        <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${active ? "bg-blue-100 text-blue-700" : "bg-slate-100 text-slate-500"}`}>
          <Shield className="h-4 w-4" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-xs font-semibold text-slate-900">{fw.name}</span>
          <span className="block text-[11px] text-slate-500">{fw.region}</span>
        </span>
        {fw.pct != null && (
          <span className="flex w-20 flex-col items-end gap-1">
            <span className="text-xs font-bold" style={{ color: tone }}>{fw.pct}%</span>
            <span className="h-1 w-full overflow-hidden rounded-full bg-slate-100">
              <span className="block h-full rounded-full" style={{ width: `${fw.pct}%`, background: tone }} />
            </span>
          </span>
        )}
      </button>
      <button
        type="button"
        onClick={() => onOpenChecklist(fw)}
        aria-label={`Open ${fw.region} checklist`}
        title={`Open ${fw.region} checklist`}
        className={`mr-0.5 flex h-8 w-7 shrink-0 items-center justify-center text-blue-600 transition-colors hover:text-blue-800 ${FOCUS}`}
      >
        <ChevronRight className="h-5 w-5" strokeWidth={2.5} />
      </button>
    </div>
  );
});

/* Counts up once via rAF, cancelled on unmount. */
function useCountUp(target, durationMs = 900) {
  const [val, setVal] = useState(0);
  const raf = useRef(null);
  useEffect(() => {
    const start = performance.now();
    const tick = (now) => {
      const t = Math.min(1, (now - start) / durationMs);
      setVal(Math.round(target * (1 - Math.pow(1 - t, 3))));
      if (t < 1) raf.current = requestAnimationFrame(tick);
    };
    raf.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf.current);
  }, [target, durationMs]);
  return val;
}

const ComplianceRing = memo(function ComplianceRing({ value, animatedValue }) {
  const r = 50, c = 2 * Math.PI * r;
  const offset = c - (animatedValue / 100) * c;
  return (
    <div className="relative flex h-28 w-28 shrink-0 items-center justify-center">
      <svg viewBox="0 0 120 120" className="h-28 w-28 -rotate-90" role="img" aria-label={`Overall compliance ${value}%`}>
        <defs>
          <linearGradient id="gcRingGrad" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#2563EB" />
            <stop offset="100%" stopColor="#10B981" />
          </linearGradient>
        </defs>
        <circle cx="60" cy="60" r={r} fill="none" stroke="#EEF2F7" strokeWidth="9" />
        <circle
          cx="60" cy="60" r={r} fill="none" stroke="url(#gcRingGrad)" strokeWidth="9" strokeLinecap="round"
          strokeDasharray={c} strokeDashoffset={offset} style={{ transition: "stroke-dashoffset .1s linear" }}
        />
      </svg>
      <div className="absolute flex flex-col items-center">
        <span className="text-2xl font-extrabold leading-none text-slate-900">{animatedValue}%</span>
        <span className="mt-1 text-[10px] font-medium text-slate-500">Overall</span>
      </div>
    </div>
  );
});

/* ------------------------------------------------------------------ */
/* Main component                                                      */
/* ------------------------------------------------------------------ */
export default function GlobalComplianceDashboard() {
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeMarker, setActiveMarker] = useState(null);
  const [hovered, setHovered] = useState(null);
  const [regionView, setRegionView] = useState(VIEW_OPTIONS[0]);
  const [statusFilter, setStatusFilter] = useState(null);
  const [showAllFrameworks, setShowAllFrameworks] = useState(false);
  const [barsAnimated, setBarsAnimated] = useState(false);
  const [allowed, setAllowed] = useState(DEFAULT_COMPLIANCE);
  const [overview, setOverview] = useState(null);
  const [position, setPosition] = useState(REGION_FOCUS["Global View"]);

  useEffect(() => {
    const t = setTimeout(() => setBarsAnimated(true), 120);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiFetch("/tenant/settings/global-compliance/overview");
        const data = await res.json();
        if (!cancelled && res.ok) {
          if (data.countries?.length) {
            setAllowed(data.countries.map((item) => ({ region: item.region, country: item.country })));
          }
          setOverview(data);
          return;
        }
      } catch {
        /* Fall through to the country-list endpoint. */
      }
      try {
        const res = await apiFetch("/tenant/settings/global-compliance");
        const data = await res.json();
        if (!cancelled && res.ok && data.selections?.length) setAllowed(data.selections);
      } catch {
        /* Keep the India default when the setting cannot be loaded. */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const allowedNames = useMemo(() => new Set(allowed.map((item) => item.country)), [allowed]);
  const regionByCountry = useMemo(() => new Map(allowed.map((item) => [item.country, item.region])), [allowed]);
  const allowedRegions = useMemo(() => new Set(allowed.map((item) => item.region)), [allowed]);
  const liveByCountry = useMemo(
    () => new Map((overview?.countries || []).map((item) => [item.country, item])),
    [overview],
  );
  const markers = useMemo(
    () => MARKERS.filter((m) => allowedNames.has(m.name)).map((m) => {
      const live = liveByCountry.get(m.name);
      if (!live) return { ...m, score: null };
      return {
        ...m,
        s: live.score != null ? (live.status || m.s) : m.s,
        score: live.score ?? null,
        regs: live.framework ? [live.framework] : m.regs,
      };
    }),
    [allowedNames, liveByCountry],
  );
  const viewOptions = useMemo(
    () => [VIEW_OPTIONS[0], ...VIEW_OPTIONS.slice(1).filter((name) => allowedRegions.has(name))],
    [allowedRegions],
  );
  const regionStats = useMemo(() => {
    const live = new Map((overview?.regions || []).map((row) => [row.name, row]));
    return REGION_STATS.filter((row) => allowedRegions.has(row.name)).map((row) => {
      const hit = live.get(row.name);
      return hit ? { ...row, pct: hit.pct } : row;
    });
  }, [allowedRegions, overview]);
  const frameworks = useMemo(() => {
    const rows = markers.flatMap((m) => m.regs.map((law) => ({ name: law, region: m.name, pct: m.score, marker: m.name })));
    return rows
      .map((row, index) => ({ row, index }))
      .sort((left, right) => {
        const leftScored = left.row.pct != null;
        const rightScored = right.row.pct != null;
        if (leftScored !== rightScored) return leftScored ? -1 : 1;
        if (leftScored && rightScored && left.row.pct !== right.row.pct) return right.row.pct - left.row.pct;
        return left.index - right.index;
      })
      .map(({ row }) => row);
  }, [markers]);

  const counts = useMemo(() => {
    const c = { g: 0, a: 0, r: 0, b: 0 };
    markers.forEach((m) => { c[m.s] += 1; });
    return c;
  }, [markers]);

  const needsAttention = counts.a + counts.r;

  /* Average of the detailed scores in scope. When none of the selected countries has a score yet
     (e.g. only Canada), fall back to the share of jurisdictions marked Fully Compliant. */
  const overallScore = useMemo(() => {
    if (!markers.length) return 0;
    const scored = markers.filter((m) => m.score != null);
    if (scored.length) return Math.round(scored.reduce((s, m) => s + m.score, 0) / scored.length);
    return Math.round((counts.g / markers.length) * 100);
  }, [markers, counts]);
  const animatedScore = useCountUp(overallScore);

  /* Stat strip follows the countries chosen in Global Compliance settings. The EU marker covers its 27 members. */
  const countriesInScope = useMemo(() => {
    const names = new Set();
    markers.forEach((m) => {
      if (m.name === "European Union") EU_MEMBERS.forEach((n) => names.add(n));
      else names.add(GEO_TO_UAE[m.name] || m.name);
    });
    return names.size;
  }, [markers]);
  const regulationsInScope = useMemo(() => {
    const laws = new Set();
    markers.forEach((m) => m.regs.forEach((law) => laws.add(law === "GDPR" ? "GDPR" : `${m.name}|${law}`)));
    return laws.size;
  }, [markers]);

  const visibleFrameworks = showAllFrameworks ? frameworks : frameworks.slice(0, 5);

  const selectRegion = useCallback((name) => {
    setRegionView(name);
    setPosition(REGION_FOCUS[name] || REGION_FOCUS["Global View"]);
  }, []);

  useEffect(() => {
    if (!viewOptions.includes(regionView)) selectRegion(viewOptions[0]);
  }, [viewOptions, regionView, selectRegion]);

  useEffect(() => {
    setActiveMarker((current) => {
      if (!current) return current;
      return markers.find((m) => m.name === current.name) || current;
    });
  }, [markers]);

  const resetView = useCallback(() => {
    setPosition(REGION_FOCUS["Global View"]);
    setRegionView(VIEW_OPTIONS[0]);
  }, []);

  const focusMarker = useCallback((m) => {
    if (!m) return;
    setStatusFilter(null);
    setRegionView(VIEW_OPTIONS[0]);
    setActiveMarker(m);
    setPosition({ coordinates: m.coordinates, zoom: 3.2 });
  }, []);

  const openChecklistForMarker = useCallback((marker) => {
    if (!marker) return;
    navigate("/user/global-compliance/checklist", {
      state: {
        country: GEO_TO_UAE[marker.name] || marker.name,
        region: marker.region,
      },
    });
  }, [navigate]);

  const handleEnter = useCallback((m) => setHovered(m), []);
  const handleLeave = useCallback(() => setHovered(null), []);
  const handlePick = useCallback((m) => openChecklistForMarker(m), [openChecklistForMarker]);
  const toggleStatus = useCallback((s) => setStatusFilter((cur) => (cur === s ? null : s)), []);

  const handleRegionBar = useCallback(
    (name) => selectRegion(regionView === name ? VIEW_OPTIONS[0] : name),
    [regionView, selectRegion],
  );

  const handleFramework = useCallback((fw) => {
    const target = markers.find((m) => m.name === fw.marker);
    if (!target) return;
    if (activeMarker?.name === target.name) {
      setActiveMarker(null);
      setPosition(REGION_FOCUS["Global View"]);
    } else {
      focusMarker(target);
    }
  }, [markers, activeMarker, focusMarker]);

  const openChecklist = useCallback((fw) => {
    openChecklistForMarker(markers.find((m) => m.name === fw.marker));
  }, [markers, openChecklistForMarker]);

  const dimmed = useCallback(
    (m) => (regionView !== VIEW_OPTIONS[0] && m.region !== regionView) || (statusFilter != null && m.s !== statusFilter),
    [regionView, statusFilter],
  );

  const activeFrameworkMarker = activeMarker?.name;

  return (
    <div className="gc-root relative -m-4 min-h-full overflow-hidden bg-[#F4F7FB] lg:-m-6">
      <style>{STYLES}</style>

      <div className="relative mx-auto max-w-[1600px] px-4 py-5 sm:px-6 sm:py-6 lg:px-8">
        {/* Header */}
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-blue-600 text-white">
              <Globe size={18} />
            </span>
            <div className="min-w-0">
              <h1 className="text-2xl font-extrabold tracking-tight text-slate-900 inline-flex items-center gap-1.5">
                Global Compliance
                <HelpTooltip
                  content="A map of the countries and privacy laws in your scope, and how each one is doing."
                  size={16}
                  position="bottom"
                  maxWidth={300}
                />
              </h1>
              <p className="text-sm font-medium text-slate-500">Stay ahead of regulations. Ensure data privacy. Build trust worldwide.</p>
            </div>
          </div>
          <span
            title="Countries currently included in your compliance scope."
            className="flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700"
          >
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-500" />
            Live · {markers.length} {markers.length === 1 ? "jurisdiction" : "jurisdictions"} in scope
          </span>
        </div>

        {/* Stats: one flat strip, hairline dividers, no shadows */}
        <div className="mb-4 grid grid-cols-2 gap-px overflow-hidden rounded-2xl border border-slate-200 bg-slate-200 lg:grid-cols-4">
          <StatCell
            icon={Globe}
            value={countriesInScope}
            label={countriesInScope === 1 ? "Country monitored" : "Countries monitored"}
            tone="bg-sky-50 text-sky-600"
            tooltip="Jurisdictions included in your compliance scope. The EU pin covers its member countries."
          />
          <StatCell
            icon={FileText}
            value={regulationsInScope}
            label={regulationsInScope === 1 ? "Regulation tracked" : "Regulations tracked"}
            tone="bg-indigo-50 text-indigo-600"
            tooltip="Privacy laws linked to the countries on this map, such as GDPR or the laws you selected in settings."
          />
          <StatCell
            icon={AlertTriangle}
            value={needsAttention}
            label={needsAttention === 1 ? "Needs attention" : "Need attention"}
            tone={needsAttention ? "bg-amber-50 text-amber-600" : "bg-slate-50 text-slate-400"}
            tooltip="Countries marked Needs Attention or Non-Compliant. Monitoring and Fully Compliant countries are not included."
          />
          <StatCell
            icon={CheckCircle2}
            value={`${animatedScore}%`}
            label="Compliance rate"
            tone="bg-emerald-50 text-emerald-600"
            progress={animatedScore}
            tooltip="How the countries on this map are doing overall. It averages each country’s privacy compliance score."
          />
        </div>

        {/* Map + insights */}
        <div className={`flex flex-col items-stretch lg:flex-row ${sidebarOpen ? "gap-4 lg:gap-5" : "gap-0"}`}>
          <div className="gc-dots relative h-[440px] w-full min-w-0 overflow-hidden rounded-2xl border border-slate-200 transition-all duration-300 sm:h-[520px] lg:h-[600px] lg:flex-1">
            <div className="absolute inset-0 cursor-grab select-none active:cursor-grabbing">
              <ComplianceMap
                markers={markers}
                allowedNames={allowedNames}
                regionByCountry={regionByCountry}
                regionView={regionView}
                statusFilter={statusFilter}
                activeName={activeMarker?.name}
                dimmed={dimmed}
                onEnter={handleEnter}
                onLeave={handleLeave}
                onPick={handlePick}
                position={position}
                onMoveEnd={setPosition}
              />
            </div>

            {/* Region chips */}
            <div role="group" aria-label="Map region" className="gc-noscroll absolute left-3 right-14 top-3 z-10 flex gap-1.5 overflow-x-auto">
              {viewOptions.map((v) => {
                const on = regionView === v;
                return (
                  <button
                    key={v}
                    type="button"
                    aria-pressed={on}
                    title={v === VIEW_OPTIONS[0] ? "Show every country in your compliance scope." : `Focus the map on ${v}.`}
                    onClick={() => selectRegion(v)}
                    className={`shrink-0 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${FOCUS} ${
                      on
                        ? "border-blue-600 bg-blue-600 text-white"
                        : "border-slate-200 bg-white text-slate-600 hover:border-blue-300 hover:text-blue-700"
                    }`}
                  >
                    {v}
                  </button>
                );
              })}
            </div>

            <button
              type="button"
              onClick={() => setSidebarOpen((v) => !v)}
              title={sidebarOpen ? "Collapse insights panel" : "Expand insights panel"}
              aria-label="Toggle insights panel"
              aria-pressed={sidebarOpen}
              className={`absolute right-3 top-3 z-10 flex h-8 w-8 items-center justify-center rounded-lg border bg-white transition-colors ${FOCUS} ${
                sidebarOpen ? "border-slate-200 text-slate-600 hover:text-blue-700" : "border-blue-600 text-blue-700"
              }`}
            >
              <PanelRight className="h-4 w-4" />
            </button>

            {/* Zoom controls */}
            <div className="absolute right-3 top-14 z-10 flex flex-col divide-y divide-slate-200 overflow-hidden rounded-lg border border-slate-200 bg-white">
              {[
                { label: "Zoom in", icon: Plus, run: () => setPosition((p) => ({ ...p, zoom: Math.min(6, +(p.zoom + 0.5).toFixed(2)) })) },
                { label: "Zoom out", icon: Minus, run: () => setPosition((p) => ({ ...p, zoom: Math.max(1, +(p.zoom - 0.5).toFixed(2)) })) },
                { label: "Reset view", icon: LocateFixed, run: resetView },
              ].map(({ label, icon: Icon, run }) => (
                <button
                  key={label}
                  type="button"
                  onClick={run}
                  aria-label={label}
                  title={label}
                  className={`flex h-8 w-8 items-center justify-center text-slate-600 transition-colors hover:bg-slate-50 hover:text-blue-700 ${FOCUS}`}
                >
                  <Icon className="h-4 w-4" />
                </button>
              ))}
            </div>

            {/* Hover readout */}
            {hovered && (
              <div className="gc-pop pointer-events-none absolute left-3 top-14 z-10 flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-800">
                <span className="h-2.5 w-2.5 rounded-full" style={{ background: STATUS_FILL[hovered.s].strong }} />
                <span>{hovered.name}</span>
                <span className="font-medium text-slate-500">{STATUS_LABEL[hovered.s]}</span>
                {hovered.score != null && <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[11px] font-bold text-slate-700">{hovered.score}%</span>}
              </div>
            )}

            {/* Legend doubles as a status filter */}
            <div role="group" aria-label="Filter pins by status" className="absolute bottom-3 left-3 right-3 z-10 flex flex-wrap gap-1 rounded-xl border border-slate-200 bg-white p-1 sm:right-auto">
              {["g", "a", "r", "b"].filter((s) => counts[s] > 0).map((s) => {
                const on = statusFilter === s;
                return (
                  <button
                    key={s}
                    type="button"
                    aria-pressed={on}
                    onClick={() => toggleStatus(s)}
                          title={
                            on
                              ? "Clear this status filter"
                              : STATUS_TIPS[s]
                          }
                    className={`flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-[11px] font-semibold transition-colors ${FOCUS} ${
                      on ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-100"
                    }`}
                  >
                    <span className="h-2.5 w-2.5 rounded-[3px]" style={{ background: STATUS_FILL[s].strong }} />
                    {STATUS_LABEL[s]}
                    <span className={`rounded px-1 text-[10px] font-bold ${on ? "bg-white/20" : "bg-slate-100 text-slate-600"}`}>{counts[s]}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <aside
            className={`flex shrink-0 flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white transition-all duration-300 ease-in-out lg:h-[600px] ${
              sidebarOpen ? "w-full opacity-100 lg:w-[340px]" : "hidden lg:flex lg:w-0 lg:border-transparent lg:opacity-0"
            }`}
          >
            <div className="gc-scroll flex w-full flex-col gap-5 p-5 lg:h-full lg:w-[340px] lg:overflow-y-auto">

              {/* Selected country: appears at the top so it is never off-screen */}
              {activeMarker && (
                <div key={activeMarker.name} className="gc-pop rounded-xl border border-blue-200 bg-blue-50/60 p-3.5">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5 text-sm font-bold text-slate-900">
                        <MapPin className="h-4 w-4 shrink-0 text-blue-600" />
                        <span className="truncate">{activeMarker.name}</span>
                      </div>
                      <span
                        title={STATUS_TIPS[activeMarker.s]}
                        className="mt-1.5 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-bold"
                        style={{ background: `${STATUS_COLOR[activeMarker.s]}1A`, color: STATUS_COLOR[activeMarker.s] }}
                      >
                        {STATUS_LABEL[activeMarker.s]}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => setActiveMarker(null)}
                      title="Clear the selected country."
                      aria-label="Clear selection"
                      className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-slate-500 transition-colors hover:bg-white hover:text-slate-800 ${FOCUS}`}
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>

                  {activeMarker.score != null ? (
                    <div className="mt-3">
                      <div className="flex items-baseline gap-1">
                        <span className="text-2xl font-extrabold text-slate-900">{activeMarker.score}</span>
                        <span className="text-xs font-medium text-slate-500" title="Detailed compliance score for this country, out of 100.">/ 100 compliance score</span>
                      </div>
                      <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-white">
                        <div className="h-full rounded-full" style={{ width: `${activeMarker.score}%`, background: scoreTone(activeMarker.score) }} />
                      </div>
                    </div>
                  ) : (
                    <p className="mt-3 text-xs text-slate-500">A detailed score isn't available for this country yet. Its status comes from ongoing monitoring.</p>
                  )}

                  {activeMarker.regs.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-1.5">
                      {activeMarker.regs.map((r) => (
                        <span key={r} title="A privacy law that applies to this country." className="rounded-md border border-blue-100 bg-white px-2 py-1 text-[11px] font-semibold text-blue-800">{r}</span>
                      ))}
                    </div>
                  )}

                  <div className="mt-3 grid grid-cols-2 gap-1.5">
                    <button
                      type="button"
                      title="Move the map to this country."
                      onClick={() => focusMarker(activeMarker)}
                      className={`flex items-center justify-center gap-1.5 rounded-lg border border-blue-200 bg-white py-1.5 text-xs font-semibold text-blue-700 transition-colors hover:bg-blue-50 ${FOCUS}`}
                    >
                      <LocateFixed className="h-3.5 w-3.5" />Zoom
                    </button>
                    <button
                      type="button"
                      title="Open the privacy checklist for this country."
                      onClick={() => openChecklist({ marker: activeMarker.name })}
                      className={`flex items-center justify-center gap-0.5 rounded-lg border border-blue-200 bg-white py-1.5 text-xs font-semibold text-blue-700 transition-colors hover:bg-blue-50 ${FOCUS}`}
                    >
                      Checklist<ChevronRight className="h-4 w-4" strokeWidth={2.5} />
                    </button>
                  </div>
                </div>
              )}

              {/* Overview */}
              <div>
                <SectionTitle
                  icon={TrendingUp}
                  tooltip="The ring is the same compliance rate as the card above. The list counts countries in each status. Select a status to filter the map."
                >
                  Global compliance overview
                </SectionTitle>
                <div className="flex items-center gap-4">
                  <ComplianceRing value={overallScore} animatedValue={animatedScore} />
                  <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                    {["g", "a", "r", "b"].map((s) => {
                      const on = statusFilter === s;
                      return (
                        <button
                          key={s}
                          type="button"
                          aria-pressed={on}
                          disabled={counts[s] === 0}
                          onClick={() => toggleStatus(s)}
                          title={STATUS_TIPS[s]}
                          className={`flex items-center justify-between rounded-lg px-2 py-1.5 text-xs transition-colors disabled:opacity-40 ${FOCUS} ${
                            on ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-50"
                          }`}
                        >
                          <span className="flex items-center gap-2 font-semibold">
                            <span className="h-2.5 w-2.5 rounded-[3px]" style={{ background: STATUS_FILL[s].strong }} />
                            {STATUS_SHORT[s]}
                          </span>
                          <span className="font-bold">{counts[s]}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Regions */}
              <div className="border-t border-slate-100 pt-4">
                <SectionTitle
                  icon={MapPin}
                  tooltip="Average compliance for the countries in each region. Select a region to focus the map."
                >
                  Compliance by region
                </SectionTitle>
                <div className="flex flex-col">
                  {regionStats.map((r) => (
                    <RegionBar
                      key={r.name}
                      name={r.name}
                      pct={r.pct}
                      animated={barsAnimated}
                      active={regionView === r.name}
                      onSelect={handleRegionBar}
                    />
                  ))}
                </div>
              </div>

              {/* Frameworks */}
              <div className="border-t border-slate-100 pt-4">
                <SectionTitle
                  icon={FileText}
                  tooltip="Laws that apply to the countries you monitor. Select a law to focus its country, or open its checklist."
                  action={frameworks.length > 5 && (
                    <button
                      type="button"
                      onClick={() => setShowAllFrameworks((v) => !v)}
                      className={`rounded text-xs font-semibold text-blue-600 hover:text-blue-800 ${FOCUS}`}
                    >
                      {showAllFrameworks ? "Show less" : `View all ${frameworks.length}`}
                    </button>
                  )}
                >
                  Privacy laws in scope
                </SectionTitle>
                <div className="flex flex-col">
                  {visibleFrameworks.map((fw) => (
                    <FrameworkRow
                      key={`${fw.marker}-${fw.name}`}
                      fw={fw}
                      active={activeFrameworkMarker === fw.marker}
                      onSelect={handleFramework}
                      onOpenChecklist={openChecklist}
                    />
                  ))}
                </div>
              </div>

              {!activeMarker && (
                <div className="mt-auto flex items-center gap-3 rounded-xl border border-dashed border-slate-200 px-3.5 py-3">
                  <MapPin className="h-4 w-4 shrink-0 text-slate-400" />
                  <p className="text-xs font-medium text-slate-500">Select a pin, or a framework above, to see country details.</p>
                </div>
              )}
            </div>
          </aside>
        </div>

        <p className="mt-4 text-xs text-slate-500">
          Map source:{" "}
          <a className="font-semibold text-blue-700 underline-offset-2 hover:underline" href="https://github.com/topojson/world-atlas" target="_blank" rel="noreferrer">
            github.com/topojson/world-atlas
          </a>
          {" "}(countries-110m.json). India state boundaries:{" "}
          <a className="font-semibold text-blue-700 underline-offset-2 hover:underline" href="https://github.com/AbhinavSwami28/india-official-geojson" target="_blank" rel="noreferrer">
            github.com/AbhinavSwami28/india-official-geojson
          </a>
          . This map is for compliance reference only.
        </p>
      </div>
    </div>
  );
}